import { FC, useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Whiteboard from "../../components/Whiteboard/Whiteboard";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import PollCreator from "../../components/PollCreator/PollCreator";
import PdfViewer from "../../components/PdfViewer/PdfViewer";
import { useLiveRoom } from "../../hooks/useLiveRoom";
import AgoraRTC, { IAgoraRTCRemoteUser } from "agora-rtc-sdk-ng";

interface DrawData {
  type: "start" | "draw" | "end";
  x: number;
  y: number;
  color: string;
  size: number;
}

interface Participant {
  id: string;
  name: string;
  handRaised: boolean;
  raisedAt?: Date;
  role?: string;
  isCameraOn?: boolean;
}

interface ChatMessage {
  message: string;
  sender: {
    userId: string;
    name: string;
    role: string;
  };
  timestamp: string | Date;
}

interface PollOption {
  id: string;
  text: string;
}

const InstructorClassroom: FC = () => {
  const { roomId } = useParams();
  const [chatMessage, setChatMessage] = useState("");
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isWhiteboardActive, setIsWhiteboardActive] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showRaisedHands, setShowRaisedHands] = useState(false);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [currentPdf, setCurrentPdf] = useState<File | null>(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [joinedParticipants, setJoinedParticipants] = useState<Participant[]>(
    []
  );
  const [leftParticipants, setLeftParticipants] = useState<Participant[]>([]);
  const raisedHands = participants.filter((p) => p.handRaised);
  
  // Debug raised hands count
  useEffect(() => {
    console.log("Raised hands count updated:", raisedHands.length);
    console.log("Raised hands participants:", raisedHands);
    console.log("All participants:", participants);
  }, [raisedHands.length, participants]);
  const navigate = useNavigate();
  // Shared Agora RTC + Socket.IO lifecycle (join, chat, connection, teardown).
  const {
    agoraServiceRef,
    streamServiceRef,
    isSocketConnected,
    messages,
    setMessages,
  } = useLiveRoom({ roomId, role: "admin" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [remoteUsers, setRemoteUsers] = useState<{ uid: string }[]>([]);
  const remoteUserRef = useRef<IAgoraRTCRemoteUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [handRaiseNotification, setHandRaiseNotification] = useState<{
    studentName: string;
    timestamp: Date;
    isRaised: boolean;
  } | null>(null);
  const [networkQuality, setNetworkQuality] = useState<{
    uplink: number;
    downlink: number;
    timestamp: Date;
  } | null>(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [courseSections, setCourseSections] = useState<any[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('');
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');
  const [isCreatingNewSection, setIsCreatingNewSection] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to play notification sound
  const playNotificationSound = () => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  };

  // Function to get network quality status
  const getNetworkQualityStatus = (quality: number) => {
    if (quality >= 4) return { status: 'Excellent', color: 'text-green-500', bgColor: 'bg-green-100' };
    if (quality >= 3) return { status: 'Good', color: 'text-blue-500', bgColor: 'bg-blue-100' };
    if (quality >= 2) return { status: 'Fair', color: 'text-yellow-500', bgColor: 'bg-yellow-100' };
    if (quality >= 1) return { status: 'Poor', color: 'text-orange-500', bgColor: 'bg-orange-100' };
    return { status: 'Very Poor', color: 'text-red-500', bgColor: 'bg-red-100' };
  };

  // Network Quality Indicator Component
  const NetworkQualityIndicator = ({ quality, label }: { quality: number; label: string }) => {
    const status = getNetworkQualityStatus(quality);
    return (
      <div className={`flex items-center space-x-2 px-2 py-1 rounded ${status.bgColor}`}>
        <div className={`w-2 h-2 rounded-full ${status.color.replace('text-', 'bg-')}`}></div>
        <span className={`text-xs font-medium ${status.color}`}>
          {label}: {status.status}
        </span>
      </div>
    );
  };

  const { data: sessionData } = useQuery({
    queryKey: ["classroom", roomId],
    queryFn: async ({ signal }) => {
      try {
      const response = await fetch(
          `${process.env.REACT_APP_API_URL}/live-classes/room/${roomId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
            signal, // Add abort signal support
        }
      );
      if (!response.ok) throw new Error("Failed to fetch session");
      return response.json();
      } catch (error: any) {
        // Handle aborted requests gracefully
        if (error.name === 'AbortError') {
          console.log('Request was aborted');
          return null;
        }
        throw error;
      }
    },
    retry: (failureCount, error: any) => {
      // Don't retry on abort errors
      if (error?.name === 'AbortError') return false;
      return failureCount < 3;
    },
    enabled: !!roomId, // Only run query when roomId is available
  });


  // Instructor-specific wiring layered on the shared useLiveRoom lifecycle.
  // The hook owns Agora/socket init, chat, connection status and teardown.
  // Once its services are connected, attach the instructor's participant/status
  // socket listeners and Agora remote-user rendering, and play the local
  // camera preview. Runs once per room.
  const instructorWiredRef = useRef(false);
  useEffect(() => {
    if (!roomId) return;
    if (!isSocketConnected || instructorWiredRef.current) return;
    if (!streamServiceRef.current || !agoraServiceRef.current) return;
    instructorWiredRef.current = true;
    setIsLoading(false);
    attachInstructorSocketListeners();
    attachInstructorAgoraHandlers();
    playInstructorLocalVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSocketConnected, roomId]);

  // Attach instructor-specific socket listeners on the hook-owned socket
  // (participant / hand-raise / status events). The shared socket lifecycle
  // (connect / join-room / chat) lives in useLiveRoom.
  const attachInstructorSocketListeners = () => {
    try {
      const service = streamServiceRef.current;
      if (!service) return;

      // Listen for participants updates
      service.socketInstance.on("participants-updated", (data: any) => {
        console.log("=== PARTICIPANTS UPDATED ===");
        console.log("Received data:", data);
        console.log("Raw participants from backend:", data.participants);
        console.log("Current participants before participants-updated:", participants);
        
        // Clean the received participants data
        const cleanedParticipants = cleanParticipants(data.participants || []);
        console.log("Cleaned participants:", cleanedParticipants);
        console.log("Participant count:", cleanedParticipants.length);
        
        setParticipants(cleanedParticipants);
        setJoinedParticipants(data.joinedParticipants || []);
        setLeftParticipants(data.leftParticipants || []);
        console.log("Participants after participants-updated:", cleanedParticipants);
        console.log("=============================");
      });

      // Listen for participant joined
      service.socketInstance.on("participant-joined", (data: any) => {
        console.log("=== PARTICIPANT JOINED ===");
        console.log("Received data:", data);
        console.log("Current participants before participant-joined:", participants);
        
        // Ensure we have a proper name
        let participantName = data.participant.name;
        if (!participantName || participantName === "undefined undefined" || participantName.includes("undefined")) {
          if (data.participant.role === "admin") {
            participantName = "Instructor";
          } else {
            participantName = `Student ${data.participant.userId}`;
          }
        }
        
        console.log("Final participant name:", participantName);
        console.log("Participant role:", data.participant.role);
        console.log("Participant userId:", data.participant.userId);
        
        setParticipants((prev) => {
          console.log("=== UPDATING PARTICIPANTS FOR JOIN ===");
          console.log("Previous participants:", prev);
          
          // First clean existing participants
          const cleanedPrev = cleanParticipants(prev);
          
          // Check if participant already exists
          const existingParticipant = cleanedPrev.find((p) => p.id === data.participant.userId);
          if (existingParticipant) {
            console.log("Participant already exists, updating name if needed:", data.participant.userId);
            // Update existing participant's name if it's better
            const updated = cleanedPrev.map(p => 
              p.id === data.participant.userId 
                ? { ...p, name: participantName } 
                : p
            );
            console.log("Updated existing participant name");
            return cleanParticipants(updated); // Clean again to ensure no issues
          }
          
          // Add new participant
          const newParticipant = {
            id: data.participant.userId,
            name: participantName,
            handRaised: false,
            isCameraOn: false, // Default to camera off
            role: data.participant.role || 'user', // Add role from backend data
          };
          console.log("Adding new participant:", newParticipant);
          const updated = [...cleanedPrev, newParticipant];
          console.log("Updated participants after participant-joined:", updated);
          console.log("Total participants count:", updated.length);
          return cleanParticipants(updated); // Clean to ensure no duplicates
        });
      });

      // Listen for participant left
      service.socketInstance.on("participant-left", (data: any) => {
        console.log("Participant left:", data);
        setParticipants((prev) =>
          prev.filter((p) => p.id !== data.participant.userId)
        );
      });

      // Listen for hand raise status changes
      service.socketInstance.on("hand-raise-status-changed", (data: any) => {
        console.log("=== HAND RAISE STATUS CHANGED ===");
        console.log("Received data:", data);
        console.log("Current participants before update:", participants);
        console.log("Looking for userId:", data.userId);
        console.log("Data types - userId:", typeof data.userId, "isRaised:", typeof data.isRaised);
        
        // Show notification for hand raise events
        if (data.isRaised) {
          setHandRaiseNotification({
            studentName: data.studentName || "Student",
            timestamp: new Date(),
            isRaised: true,
          });
          
          // Play notification sound
          playNotificationSound();
          
          // Auto-hide notification after 5 seconds
          setTimeout(() => {
            setHandRaiseNotification(null);
          }, 5000);
        }
        
        setParticipants((prev) => {
          console.log("=== UPDATING PARTICIPANTS FOR HAND RAISE ===");
          console.log("Previous participants:", prev);
          console.log("Looking for userId:", data.userId);
          
          // First clean existing participants
          const cleanedPrev = cleanParticipants(prev);
          
          // Only update existing participants - don't create new ones
          const updated = cleanedPrev.map((p) => {
            // Ensure both IDs exist before comparing
            if (!p.id || !data.userId) {
              return p;
            }
            
            // Try to match by different ID formats
            const matches = p.id === data.userId || 
                           p.id === String(data.userId) || 
                           String(p.id) === data.userId ||
                           String(p.id) === String(data.userId);
            
            if (matches) {
              console.log(`Hand raise updated for participant ${p.name}: ${data.isRaised}`);
              return { ...p, handRaised: data.isRaised, raisedAt: data.isRaised ? new Date() : undefined };
            }
            return p;
          });
          
          console.log("Updated participants after hand raise:", updated);
          console.log("Raised hands count:", updated.filter(p => p.handRaised).length);
          return cleanParticipants(updated); // Clean to ensure no issues
        });
      });

      // Listen for instructor control actions (for future use)
      service.socketInstance.on("instructor-control-action", (data: any) => {
        console.log("Instructor control action:", data);
        // Handle instructor control actions if needed
      });

      // Listen for camera status changes from students
      service.socketInstance.on("camera-status-changed", (data: any) => {
        console.log("=== CAMERA STATUS CHANGED EVENT RECEIVED ===");
        console.log("Received data:", data);
        console.log("Data role:", data.role);
        console.log("Data userId:", data.userId);
        console.log("Data isCameraOff:", data.isCameraOff);
        console.log("Current participants:", participants);
        
        if (data.role === 'user' && data.userId) {
          console.log("Processing camera toggle for user:", data.userId);
          // Update participant's camera status
          setParticipants((prev) => {
            console.log("Previous participants:", prev);
            const updated = prev.map((participant) => {
              console.log(`Checking participant: ${participant.name} (ID: ${participant.id}) against ${data.userId}`);
              if (participant.id === data.userId) {
                const newStatus = !data.isCameraOff;
                console.log(`✅ MATCH FOUND! Updating participant ${participant.name} camera status to: ${newStatus ? 'ON' : 'OFF'}`);
                return { ...participant, isCameraOn: newStatus };
              }
              return participant;
            });
            console.log("Updated participants:", updated);
            return updated;
          });
          console.log(`Updated camera status for user ${data.userId}: ${!data.isCameraOff ? 'ON' : 'OFF'}`);
        } else {
          console.log("Ignoring camera toggle event - not from a user or missing userId");
        }
      });

      // Chat receive (dedupe by current user) is handled by useLiveRoom.

      // These listeners attach after the socket has already connected and
      // join-room fired, so request a fresh participants broadcast to sync the
      // current room state (closes the race on any participants-updated events
      // emitted before this point).
      service.socketInstance.emit("participants-list", { roomId });
    } catch (error) {
      console.error("Instructor socket listener attach failed:", error);
    }
  };

  // Play the instructor's local camera preview into #camera-video once the hook
  // has created & published the local track (replaces the joinAsHost callback).
  const playInstructorLocalVideo = (attempt = 0) => {
    const agoraService = agoraServiceRef.current;
    const videoElement = document.getElementById("camera-video");
    if (videoElement && agoraService?.localVideoTrack) {
      try {
        agoraService.localVideoTrack.play(videoElement);
        setIsCameraOn(true);
      } catch (error) {
        console.error("Error playing local video:", error);
      }
    } else if (attempt < 10) {
      // The hook publishes local tracks asynchronously after join; retry briefly.
      setTimeout(() => playInstructorLocalVideo(attempt + 1), 500);
    }
  };

  // Attach instructor-specific Agora handlers (remote-user rendering + network
  // quality) on the hook-owned Agora client. Init/join/reconnect live in useLiveRoom.
  const attachInstructorAgoraHandlers = () => {
    const agoraService = agoraServiceRef.current;
    if (!agoraService || !agoraService.getClient()) return;
    try {
      //handle remote users with enhanced error handling
      agoraService
        .getClient()
        ?.on("user-published", async (user, mediaType) => {
          try {
            console.log("=== USER PUBLISHED ===");
            console.log("User UID:", user.uid);
            console.log("User UID type:", typeof user.uid);
            console.log("Media type:", mediaType);
            console.log("User object:", user);
            console.log("Current participants:", participants.map(p => ({ id: p.id, name: p.name })));
            console.log("Current remote users:", remoteUsers.map(u => u.uid));
            
            await agoraService.getClient()?.subscribe(user, mediaType);

            if (mediaType === "video") {
              setRemoteUsers((prev) => {
                if (prev.some((u) => u.uid === user.uid.toString())) {
                  console.log("User already in remoteUsers list");
                  return prev;
                }
                console.log("Adding user to remoteUsers:", user.uid.toString());
                return [...prev, { uid: user.uid.toString() }];
              });

              // Wait for DOM to update before playing video
              setTimeout(() => {
                // Try to find the video element using different UID formats
                const userUid = typeof user.uid === 'string' ? parseInt(user.uid) : user.uid;
                let remoteVideo = null;
                let videoElementId = '';
                
                // First try the exact UID
                videoElementId = `remote-video-${user.uid}`;
                remoteVideo = document.getElementById(videoElementId);
                
                // If not found, try to find by base UID (without timestamp)
                if (!remoteVideo) {
                  // Find all video elements and check which one matches
                  const allVideoElements = Array.from(document.querySelectorAll('[id^="remote-video-"]'));
                  for (const element of allVideoElements) {
                    const elementId = element.id;
                    const elementUid = parseInt(elementId.replace('remote-video-', ''));
                    
                    // Check if this element's UID matches our user UID (with or without timestamp)
                    if (elementUid === userUid || Math.abs(elementUid - userUid) < 10000) {
                      remoteVideo = element as HTMLElement;
                      videoElementId = elementId;
                      break;
                    }
                  }
                }
                
                console.log("=== INSTRUCTOR VIDEO ELEMENT DEBUG ===");
                console.log("User UID:", user.uid, "(" + userUid + ")");
                console.log("Looking for video element ID:", videoElementId);
                console.log("Found element:", remoteVideo);
                console.log("Element has child nodes:", remoteVideo?.hasChildNodes());
                console.log("User video track:", user.videoTrack);
                console.log("Video track exists:", !!user.videoTrack);
                console.log("=====================================");
                
                if (remoteVideo) {
                  // Play video if element is empty
                  if (!remoteVideo.hasChildNodes()) {
                    user.videoTrack?.play(remoteVideo);
                    console.log("Playing remote video for user:", user.uid);
                    
                    // Hide the camera off overlay by adding a class or removing the overlay
                    const overlay = remoteVideo.parentElement?.querySelector('.camera-off-overlay') as HTMLElement;
                    if (overlay) {
                      overlay.style.display = 'none';
                      console.log("Hidden camera off overlay for user:", user.uid);
                    }
                  } else {
                    console.log("Video element already has content for user:", user.uid);
                  }
                } else {
                  console.log("Could not find video element for user:", user.uid);
                  console.log("Available video elements:", Array.from(document.querySelectorAll('[id^="remote-video-"]')).map(el => el.id));
                }
              }, 100);
            }

            if (mediaType === "audio") {
              // Play audio immediately after subscription with volume control
              if (user.audioTrack) {
                user.audioTrack.play();
                // Set default volume (can be controlled by students)
                user.audioTrack.setVolume(50);
                console.log("Playing remote audio for user:", user.uid);
              }
            }
            console.log("========================");
          } catch (error) {
            console.error("Error handling user-published:", error);
          }
        });

      // Add user-unpublished handler to stop audio and clean up
      agoraService.getClient()?.on("user-unpublished", (user, mediaType) => {
        try {
          console.log("=== USER UNPUBLISHED ===");
          console.log("User UID:", user.uid);
          console.log("Media type:", mediaType);
          
          if (mediaType === "audio" && user.audioTrack) {
            user.audioTrack?.stop();
            console.log("Stopped audio for user:", user.uid);
          }
          if (mediaType === "video") {
            setRemoteUsers((prev) => {
              const filtered = prev.filter((u) => u.uid !== user.uid.toString());
              console.log("Removed user from remoteUsers:", user.uid.toString());
              console.log("Remaining remoteUsers:", filtered);
              return filtered;
            });
          }
          console.log("========================");
        } catch (error) {
          console.error("Error handling user-unpublished:", error);
        }
      });

      // Add network quality monitoring
      agoraService.getClient()?.on("network-quality", (stats) => {
        console.log("=== NETWORK QUALITY UPDATE ===");
        console.log("Uplink quality:", stats.uplinkNetworkQuality);
        console.log("Downlink quality:", stats.downlinkNetworkQuality);
        
        setNetworkQuality({
          uplink: stats.uplinkNetworkQuality,
          downlink: stats.downlinkNetworkQuality,
          timestamp: new Date()
        });
        
        // Log warnings only for poor or very poor quality (not fair)
        if (stats.uplinkNetworkQuality <= 1) {
          console.warn("Poor uplink quality detected:", stats.uplinkNetworkQuality);
        }
        if (stats.downlinkNetworkQuality <= 1) {
          console.warn("Poor downlink quality detected:", stats.downlinkNetworkQuality);
        }
        console.log("==============================");
      });
    } catch (error) {
      console.error("Instructor Agora handler attach failed:", error);
    }
  };

  // Enhanced control handlers with proper track management
  const toggleMic = async () => {
    try {
      if (agoraServiceRef.current?.localAudioTrack) {
        const newMicState = !isMicOn;
        if (newMicState) {
          // Unmute microphone
          await agoraServiceRef.current.localAudioTrack.setMuted(false);
        } else {
          // Mute microphone
          await agoraServiceRef.current.localAudioTrack.setMuted(true);
        }
        setIsMicOn(newMicState);
        console.log(`Microphone ${newMicState ? "unmuted" : "muted"}`);

        // Emit WebSocket event to notify all participants
        if (streamServiceRef.current?.socketInstance && roomId) {
          streamServiceRef.current.socketInstance.emit('mic-toggle', {
            roomId,
            userId: sessionData?.instructorId?._id || 'instructor',
            isMuted: !newMicState,
            role: 'instructor',
          });
        }
      } else {
        console.warn("No audio track available");
      }
    } catch (error) {
      console.error("Error toggling microphone:", error);
      alert("Failed to toggle microphone. Please try again.");
    }
  };

  const toggleCamera = async () => {
    try {
      if (agoraServiceRef.current?.localVideoTrack) {
        const newCameraState = !isCameraOn;
        if (newCameraState) {
          // Turn on camera by unmuting video track
          await agoraServiceRef.current.localVideoTrack.setMuted(false);
        } else {
          // Turn off camera by muting video track
          await agoraServiceRef.current.localVideoTrack.setMuted(true);
        }
        setIsCameraOn(newCameraState);
        console.log(`Camera ${newCameraState ? "turned on" : "turned off"}`);

        // Emit WebSocket event to notify all participants
        if (streamServiceRef.current?.socketInstance && roomId) {
          streamServiceRef.current.socketInstance.emit('camera-toggle', {
            roomId,
            userId: sessionData?.instructorId?._id || 'instructor',
            isCameraOff: !newCameraState,
            role: 'instructor',
          });
        }
      } else {
        console.warn("No video track available");
      }
    } catch (error) {
      console.error("Error toggling camera:", error);
      alert("Failed to toggle camera. Please try again.");
    }
  };

  const startScreenSharing = async () => {
    try {
      if (agoraServiceRef.current) {
        // Check if screen sharing is supported
        if (!navigator.mediaDevices.getDisplayMedia) {
          throw new Error("Screen sharing is not supported in your browser");
        }

        // Create screen video track with proper error handling
        let screenTrackResult;
        try {
          screenTrackResult = await AgoraRTC.createScreenVideoTrack(
            {
              // Screen share optimization - avoid min constraints (not supported by getDisplayMedia)
              encoderConfig: {
                width: { ideal: 1920, max: 1920 },
                height: { ideal: 1080, max: 1080 },
                frameRate: { ideal: 15, max: 30 },
                bitrateMin: 1000,
                bitrateMax: 3000,
              },
              optimizationMode: "detail", // Better for screen content
            },
            "auto"
          );
        } catch (error: any) {
          if (error.name === "NotAllowedError") {
            throw new Error("Screen sharing permission denied");
          } else if (error.name === "NotSupportedError") {
            throw new Error("Screen sharing is not supported");
          } else if (error.message?.includes("min constraints are not supported")) {
            console.warn("Min constraints not supported, trying basic constraints");
            // Fallback to very basic constraints
            screenTrackResult = await AgoraRTC.createScreenVideoTrack(
              {
                encoderConfig: {
                  width: { ideal: 1280 },
                  height: { ideal: 720 },
                  frameRate: { ideal: 15 },
                },
              },
              "auto"
            );
          } else {
          throw error;
          }
        }

        // Handle single track or array of tracks
        const screenVideoTrack = Array.isArray(screenTrackResult)
          ? screenTrackResult[0]
          : screenTrackResult;

        // Unpublish camera video track if it exists
        if (agoraServiceRef.current.localVideoTrack) {
          await agoraServiceRef.current
            .getClient()
            ?.unpublish(agoraServiceRef.current.localVideoTrack);
        }

        // Publish screen track
        await agoraServiceRef.current.getClient()?.publish(screenVideoTrack);

        // Play screen track in video element
        const videoElement = document.getElementById("camera-video");
        if (videoElement) {
          screenVideoTrack.play(videoElement);
        }

        // Handle screen share ending (user clicks stop sharing in browser)
        screenVideoTrack.on("track-ended", () => {
          console.log("Screen sharing ended by user");
          stopScreenSharing();
        });

        // Store screen track reference
        agoraServiceRef.current.screenTrack = screenVideoTrack;
        setIsScreenSharing(true);
        console.log("Screen sharing started successfully");

        // Emit WebSocket event to notify all participants
        if (streamServiceRef.current?.socketInstance && roomId) {
          streamServiceRef.current.socketInstance.emit('screen-share-toggle', {
            roomId,
            userId: sessionData?.instructorId?._id || 'instructor',
            isSharing: true,
            role: 'instructor',
          });
        }
      }
    } catch (error: any) {
      console.error("Error starting screen share:", error);
      setIsScreenSharing(false);
      alert(
        error.message || "Failed to start screen sharing. Please try again."
      );
    }
  };

  const stopScreenSharing = async () => {
    try {
      if (agoraServiceRef.current?.screenTrack) {
        // Unpublish screen track
        await agoraServiceRef.current
          .getClient()
          ?.unpublish(agoraServiceRef.current.screenTrack);

        // Stop and close screen track
        agoraServiceRef.current.screenTrack.stop();
        agoraServiceRef.current.screenTrack.close();
        agoraServiceRef.current.screenTrack = null;

        // Re-publish camera video if available
        if (agoraServiceRef.current.localVideoTrack) {
          await agoraServiceRef.current
            .getClient()
            ?.publish(agoraServiceRef.current.localVideoTrack);

          // Play camera video back in the element
          const videoElement = document.getElementById("camera-video");
          if (videoElement) {
            agoraServiceRef.current.localVideoTrack.play(videoElement);
          }
        }

        setIsScreenSharing(false);
        console.log("Screen sharing stopped successfully");

        // Emit WebSocket event to notify all participants
        if (streamServiceRef.current?.socketInstance && roomId) {
          streamServiceRef.current.socketInstance.emit('screen-share-toggle', {
            roomId,
            userId: sessionData?.instructorId?._id || 'instructor',
            isSharing: false,
            role: 'instructor',
          });
        }
      }
    } catch (error) {
      console.error("Error stopping screen share:", error);
      setIsScreenSharing(false);
    }
  };

  const startWhiteboardScreenShare = async () => {
    try {
      console.log("=== STARTING WHITEBOARD SCREEN SHARE ===");
      
      // Get the whiteboard container element
      const whiteboardContainer = document.querySelector('.whiteboard-container');
      console.log("Whiteboard container found:", !!whiteboardContainer);
      if (!whiteboardContainer) {
        throw new Error("Whiteboard container not found");
      }

      // Get the whiteboard canvas element
      const whiteboardCanvas = whiteboardContainer.querySelector('canvas');
      console.log("Whiteboard canvas found:", !!whiteboardCanvas);
      if (!whiteboardCanvas) {
        throw new Error("Whiteboard canvas not found");
      }
      
      // Check canvas dimensions and content
      console.log("Canvas dimensions:", {
        width: whiteboardCanvas.width,
        height: whiteboardCanvas.height,
        clientWidth: whiteboardCanvas.clientWidth,
        clientHeight: whiteboardCanvas.clientHeight
      });
      
      // Check if canvas has content
      const ctx = whiteboardCanvas.getContext('2d');
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, whiteboardCanvas.width, whiteboardCanvas.height);
        const hasContent = imageData.data.some(pixel => pixel !== 0);
        console.log("Canvas has content:", hasContent);
      }

      let stream;
      let videoTrack;

      // Try canvas capture first
      try {
        console.log("Attempting canvas capture...");
        stream = whiteboardCanvas.captureStream(30); // 30 FPS
        console.log("Canvas stream created:", !!stream);
        console.log("Stream tracks:", stream.getTracks().length);
        
        if (!stream) {
          throw new Error("Failed to capture whiteboard stream");
        }

        videoTrack = stream.getVideoTracks()[0];
        console.log("Video track found:", !!videoTrack);
        if (!videoTrack) {
          throw new Error("No video track found in whiteboard stream");
        }
      } catch (canvasError) {
        console.log("Canvas capture failed, trying alternative approach:", canvasError);
        
        // Alternative: Use getDisplayMedia to capture screen
        try {
          console.log("Attempting screen capture...");
          const screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: false
          });
          
          videoTrack = screenStream.getVideoTracks()[0];
          console.log("Screen capture video track found:", !!videoTrack);
        } catch (screenError) {
          console.error("Screen capture also failed:", screenError);
          throw new Error("Both canvas and screen capture failed");
        }
      }

      // Create Agora video track from the video track
      const agoraVideoTrack = AgoraRTC.createCustomVideoTrack({
        mediaStreamTrack: videoTrack,
      });
      console.log("Agora video track created:", !!agoraVideoTrack);

      // Unpublish camera video track if it exists
      if (agoraServiceRef.current?.localVideoTrack) {
        console.log("Unpublishing camera video track");
        await agoraServiceRef.current
          .getClient()
          ?.unpublish(agoraServiceRef.current.localVideoTrack);
      }

      // Publish the whiteboard video track
      console.log("Publishing whiteboard video track");
      await agoraServiceRef.current?.getClient()?.publish(agoraVideoTrack);
      console.log("Whiteboard video track published successfully");
      
      // Add event listeners to track publishing status
      agoraVideoTrack.on("track-ended", () => {
        console.log("Whiteboard video track ended");
      });
      
      // Check if the track is actually being published
      setTimeout(() => {
        console.log("Whiteboard track state:", {
          enabled: agoraVideoTrack.enabled,
          muted: agoraVideoTrack.muted,
          trackId: agoraVideoTrack.getTrackId()
        });
      }, 1000);

      // Play whiteboard track in video element
      const videoElement = document.getElementById("camera-video");
      if (videoElement) {
        agoraVideoTrack.play(videoElement);
      }

      // Store screen track reference
      if (agoraServiceRef.current) {
        agoraServiceRef.current.screenTrack = agoraVideoTrack;
      }
      setIsScreenSharing(true);
      
      // Notify students about screen sharing
      if (streamServiceRef.current) {
        streamServiceRef.current.socketInstance.emit("screen-share-status-changed", {
          roomId,
          isScreenSharing: true,
          instructorId: sessionData?.instId,
        });
      }
    } catch (error) {
      console.error("Error starting whiteboard screen share:", error);
      throw error;
    }
  };

  const handleWhiteboardToggle = async () => {
    try {
      if (!isWhiteboardActive) {
        // Set active first
        setIsWhiteboardActive(true);

        // Wait for canvas to mount and ensure it's visible
        await new Promise((resolve) => setTimeout(resolve, 500));
        
        // Check if whiteboard is visible
        const whiteboardContainer = document.querySelector('.whiteboard-container');
        if (whiteboardContainer) {
          const computedStyle = window.getComputedStyle(whiteboardContainer);
          console.log("Whiteboard container display:", computedStyle.display);
          console.log("Whiteboard container visibility:", computedStyle.visibility);
        }
        
        // Start screen sharing the whiteboard area
        await startWhiteboardScreenShare();
        
        // Notify all students that whiteboard is now active
        if (streamServiceRef.current) {
          streamServiceRef.current.socketInstance.emit("whiteboard-toggle", {
            roomId,
            isActive: true,
            instructorId: sessionData?.instId,
          });
          
          // Send a chat notification to students
          streamServiceRef.current.socketInstance.emit("chat-message", {
            roomId,
            message: "📝 Whiteboard is now active! The instructor is sharing the whiteboard.",
            sender: {
              userId: sessionData?.instId || 'instructor',
              name: 'System',
              role: 'system'
            }
          });
        }
      } else {
        // Stop screen sharing first
        await stopScreenSharing();
        
        setIsWhiteboardActive(false);
        
        // Notify all students that whiteboard is now hidden
        if (streamServiceRef.current) {
          streamServiceRef.current.socketInstance.emit("whiteboard-toggle", {
            roomId,
            isActive: false,
            instructorId: sessionData?.instId,
          });
          
          // Send a chat notification to students
          streamServiceRef.current.socketInstance.emit("chat-message", {
            roomId,
            message: "📝 Whiteboard has been hidden.",
            sender: {
              userId: sessionData?.instId || 'instructor',
              name: 'System',
              role: 'system'
            }
          });
        }
      }
    } catch (error) {
      console.error("Error sharing whiteboard:", error);
      setIsWhiteboardActive(false);
    }
  };

  // Instructor control functions
  const handleMuteAllStudents = () => {
    if (streamServiceRef.current?.socketInstance && roomId) {
      streamServiceRef.current.socketInstance.emit('instructor-controls', {
        roomId,
        action: 'mute-all',
        instructorId: sessionData?.instructorId?._id || 'instructor',
      });
    }
  };

  const handleUnmuteAllStudents = () => {
    if (streamServiceRef.current?.socketInstance && roomId) {
      streamServiceRef.current.socketInstance.emit('instructor-controls', {
        roomId,
        action: 'unmute-all',
        instructorId: sessionData?.instructorId?._id || 'instructor',
      });
    }
  };

  const handleMuteStudent = (studentId: string) => {
    if (streamServiceRef.current?.socketInstance && roomId) {
      streamServiceRef.current.socketInstance.emit('instructor-controls', {
        roomId,
        action: 'mute-student',
        targetUserId: studentId,
        instructorId: sessionData?.instructorId?._id || 'instructor',
      });
    }
  };

  const handleUnmuteStudent = (studentId: string) => {
    if (streamServiceRef.current?.socketInstance && roomId) {
      streamServiceRef.current.socketInstance.emit('instructor-controls', {
        roomId,
        action: 'unmute-student',
        targetUserId: studentId,
        instructorId: sessionData?.instructorId?._id || 'instructor',
      });
    }
  };

  const handleLowerAllHands = () => {
    console.log("=== INSTRUCTOR LOWERING ALL HANDS ===");
    console.log("Lowering all hands for participants:", participants.filter(p => p.handRaised));
    
    if (streamServiceRef.current?.socketInstance && roomId) {
      // Emit WebSocket event to lower all hands
      streamServiceRef.current.socketInstance.emit("instructor-lower-all-hands", {
        roomId,
        instructorId: sessionData?.instructorId?._id || 'instructor',
      });
      
      // Update local state immediately for better UX
      setParticipants((prev) =>
        prev.map((p) => ({ ...p, handRaised: false, raisedAt: undefined }))
      );
      
      console.log("All hands lowered locally");
    } else {
      console.error("Cannot lower all hands: missing socket connection or roomId");
    }
  };


  // Whiteboard drawing functions are no longer needed since we're using screen sharing
  const handleWhiteboardDraw = (drawData: DrawData) => {
    // No need to broadcast drawing data since students see it via screen share
  };

  const handleWhiteboardClear = () => {
    // No need to broadcast clear events since students see it via screen share
  };

  const handleEndSession = async () => {
    // Confirmation is already handled by the ConfirmModal that invokes this
    // (via handleEndSessionWithRecording); no redundant native confirm here.
    try {
      setIsLoading(true);

      // Turn off camera and microphone before ending session
      if (agoraServiceRef.current) {
        await agoraServiceRef.current.turnOffCamera();
        await agoraServiceRef.current.turnOffMicrophone();
        await agoraServiceRef.current.leaveChannel();
      }

      // Stop the video element and clean up media streams
      const videoElement = document.querySelector("video") as HTMLVideoElement;
      if (videoElement) {
        if (videoElement.srcObject) {
          const stream = videoElement.srcObject as MediaStream;
          stream.getTracks().forEach((track) => {
            track.stop();
          });
        }
        videoElement.srcObject = null;
        videoElement.src = "";
      }

      // Emit end session event to all participants
      streamServiceRef.current?.socketInstance.emit("end-session", { roomId });

      // Stop streaming and socket connection
      streamServiceRef.current?.stopStreaming();
      streamServiceRef.current?.socketInstance.disconnect();
      streamServiceRef.current = null;

      // Reset all states (socket disconnect above flips isSocketConnected via the hook)
      setIsCameraOn(false);
      setIsMicOn(false);
      setIsScreenSharing(false);
      setRemoteUsers([]);

      // Show success message
      alert("Session ended successfully");

      // Navigate away
      navigate("/admin/live-sessions");
    } catch (error) {
      console.error("Failed to end session:", error);
      setIsLoading(false);
      alert("Failed to end session. Please try again.");
    }
  };

  // Recording functions
  const startRecording = async () => {
    try {
      // Get the video container element
      const videoContainer = document.querySelector('.video-grid-container') as HTMLElement;
      if (!videoContainer) {
        alert("Video container not found. Please try again.");
        return;
      }

      // Get the video stream from the video container
      const videoElement = videoContainer.querySelector('video') as HTMLVideoElement;
      if (!videoElement || !videoElement.srcObject) {
        alert("No video stream available to record.");
        return;
      }

      const videoStream = videoElement.srcObject as MediaStream;
      
      // Use the video stream directly for recording
      const mediaRecorder = new MediaRecorder(videoStream, {
        mimeType: 'video/webm;codecs=vp9'
      });

      const chunks: BlobPart[] = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        setRecordedBlob(blob);
        // Don't stop the video stream tracks as they're still needed for the live session
      };

      // Start recording
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);

      // Start recording timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      console.log("Recording started - capturing video stream only");
    } catch (error) {
      console.error("Failed to start recording:", error);
      alert("Failed to start recording. Please try again.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      console.log("Recording stopped");
      
      // Show upload modal immediately after stopping recording
      setTimeout(() => {
        if (recordedBlob) {
          setShowUploadModal(true);
          loadCourseSections(); // Load course sections when modal opens
        }
      }, 1000); // Small delay to ensure blob is ready
    }
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const loadCourseSections = async () => {
    try {
      const courseId = sessionData?.courseId?._id || sessionData?.courseId;
      if (!courseId) return;

      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/courses/${courseId}/content`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const courseContent = await response.json();
        setCourseSections(courseContent || []);
      }
    } catch (error) {
      console.error('Failed to load course sections:', error);
    }
  };

  const handleEndSessionWithRecording = async () => {
    if (isRecording) {
      stopRecording();
    }
    
    // Proceed with normal end session (upload modal is handled in stopRecording)
    await handleEndSession();
  };

  const uploadRecording = async (title: string, description: string) => {
    if (!recordedBlob) return;

    try {
      setIsLoading(true);
      
      // Get the course ID from the session data
      const courseId = sessionData?.courseId?._id || sessionData?.courseId;
      if (!courseId) {
        throw new Error('Course ID not found');
      }

      // Convert blob to base64 for direct inclusion in the content
      const base64Video = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(recordedBlob);
      });

      // Create a new lesson object with base64 video
      const newLesson = {
        title,
        description,
        videoUrl: base64Video, // Use base64 video directly
        duration: recordingTime, // Duration in seconds
        isFree: false,
        resources: []
      };

      // Get current course content
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      const contentResponse = await fetch(`${process.env.REACT_APP_API_URL}/courses/${courseId}/content`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!contentResponse.ok) {
        throw new Error('Failed to get course content');
      }

      const courseContent = await contentResponse.json();
      
      // Handle section selection
      const updatedContent = [...courseContent];
      
      if (isCreatingNewSection) {
        // Create a new section
        const newSection = {
          title: newSectionTitle || "New Section",
          description: newSectionDescription || "Created from recorded session",
          lessons: [newLesson]
        };
        updatedContent.push(newSection);
      } else if (selectedSectionId) {
        // Add to selected existing section
        const sectionIndex = updatedContent.findIndex(section => section._id === selectedSectionId);
        if (sectionIndex !== -1) {
          updatedContent[sectionIndex].lessons.push(newLesson);
        } else {
          // Fallback: add to first section
          if (updatedContent.length > 0) {
            updatedContent[0].lessons.push(newLesson);
          } else {
            // Create default section
            updatedContent.push({
              title: "Recorded Sessions",
              description: "Live classroom recordings",
              lessons: [newLesson]
            });
          }
        }
      } else {
        // Default behavior: add to first section or create one
        if (updatedContent.length === 0) {
          updatedContent.push({
            title: "Recorded Sessions",
            description: "Live classroom recordings",
            lessons: [newLesson]
          });
        } else {
          updatedContent[0].lessons.push(newLesson);
        }
      }

      // Update course content using the PATCH endpoint
      const updateResponse = await fetch(`${process.env.REACT_APP_API_URL}/courses/${courseId}/content`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: updatedContent
        })
      });

      if (updateResponse.ok) {
        alert("Lesson created successfully! Students can now access this recorded session as a course lesson.");
        setShowUploadModal(false);
        setRecordedBlob(null);
        // Reset form state
        setSelectedSectionId('');
        setNewSectionTitle('');
        setNewSectionDescription('');
        setIsCreatingNewSection(false);
        // Don't end session automatically - let instructor continue teaching
      } else {
        throw new Error('Failed to update course content');
      }
    } catch (error) {
      console.error("Failed to upload recording:", error);
      alert("Failed to upload recording. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = () => {
    if (!chatMessage.trim() || !streamServiceRef.current) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    // Create message object
    const newMessage: ChatMessage = {
      message: chatMessage.trim(),
      sender: {
        userId: sessionData.instId,
        name: user.username,
        role: "admin",
      },
      timestamp: new Date().toISOString(),
    };

    // Update local state immediately
    setMessages((prev) => [...prev, newMessage]);

    // Send through socket
    streamServiceRef.current.socketInstance.emit("chat-message", {
      roomId,
      message: chatMessage.trim(),
      sender: newMessage.sender,
    });

    // Clear input
    setChatMessage("");

  // Force scroll to bottom
  setTimeout(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, 0);
  };

  const handleLowerHand = (participantId: string) => {
    console.log("=== INSTRUCTOR LOWERING HAND ===");
    console.log("Lowering hand for participant:", participantId);
    
    if (streamServiceRef.current?.socketInstance && roomId) {
      // Find the participant to get their name
      const participant = participants.find(p => p.id === participantId);
      const participantName = participant?.name || "Student";
      
      console.log("Sending lower hand command for:", participantName);
      
      // Emit WebSocket event to lower the student's hand
      streamServiceRef.current.socketInstance.emit("instructor-lower-hand", {
        roomId,
        targetUserId: participantId,
        studentName: participantName,
        instructorId: sessionData?.instructorId?._id || 'instructor',
      });
      
      // Also update local state immediately for better UX
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === participantId
            ? { ...p, handRaised: false, raisedAt: undefined }
            : p
        )
      );
      
      console.log("Hand lowered locally for participant:", participantName);
    } else {
      console.error("Cannot lower hand: missing socket connection or roomId");
    }
  };

  const handlePdfUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.includes("pdf")) {
      alert("Please select a valid PDF file");
      return;
    }

    setCurrentPdf(file);
    setShowPdfViewer(true);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCreatePoll = (question: string, options: PollOption[]) => {
    // TODO: Implement WebSocket send for poll
    console.log("Creating poll:", { question, options });
  };

  // Removed duplicate participants-updated listener - using the one in initializeStreamService

  // Chat message listener is now set up in socketMethods function

  // Auto scroll chat when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Track if instructor has already joined to prevent duplicate emissions
  const instructorJoinedRef = useRef(false);

  // Emit participant-joined event once both sessionData AND the shared socket
  // are ready. Gating on isSocketConnected (and keeping it in the deps) means
  // that if sessionData resolves before the socket is up, the effect re-runs
  // and still emits once the socket connects. instructorJoinedRef keeps it
  // one-time (no re-emit on reconnect).
  useEffect(() => {
    if (sessionData && isSocketConnected && streamServiceRef.current && roomId && !instructorJoinedRef.current) {
      // Get instructor name from sessionData
      let instructorName = "Instructor";
      if (sessionData.instructorId) {
        if (sessionData.instructorId.firstName && sessionData.instructorId.lastName) {
          instructorName = `${sessionData.instructorId.firstName} ${sessionData.instructorId.lastName}`;
        } else if (sessionData.instructorId.username) {
          instructorName = sessionData.instructorId.username;
        } else if (sessionData.instructorId.name) {
          instructorName = sessionData.instructorId.name;
        }
      }
      
      // Get user ID from sessionData
      const userId = sessionData.instructorId?._id || sessionData.instId;
      
      if (userId) {
        streamServiceRef.current.socketInstance.emit("participant-joined", {
          roomId: roomId,
          participant: {
            userId: userId.toString(),
            name: instructorName,
            role: "admin",
          },
        });
        console.log("Emitted participant-joined for instructor:", instructorName);
        instructorJoinedRef.current = true; // Mark as joined to prevent duplicate emissions
      }
    }
  }, [sessionData, roomId, isSocketConnected]);

  // Comprehensive cleanup function to prevent duplicates and undefined participants
  const cleanParticipants = (participants: any[]): Participant[] => {
    // Step 1: Filter out undefined/invalid participants and normalize structure
    const filtered = participants.filter(p => 
      p && 
      (p.id || p.userId) && 
      p.name && 
      !p.name.includes("undefined") && 
      p.name.trim() !== "" &&
      (p.id || p.userId).toString().trim() !== ""
    ).map(p => ({
      // Normalize the participant structure to match frontend expectations
      id: p.id || p.userId,
      name: p.name,
      handRaised: p.handRaised || false,
      raisedAt: p.raisedAt,
      isCameraOn: p.isCameraOn || false,
      role: p.role || 'user' // Add role mapping
    }));
    
    // Step 2: Remove duplicates by ID, keeping the most complete data
    const unique = filtered.reduce((acc, current) => {
      const existingIndex = acc.findIndex(p => p.id === current.id);
      if (existingIndex === -1) {
        // New participant, add it
        acc.push(current);
      } else {
        // Duplicate found, merge data intelligently
        const existing = acc[existingIndex];
        acc[existingIndex] = {
          ...existing,
          // Keep the better name (longer, more descriptive)
          name: current.name.length > existing.name.length ? current.name : existing.name,
          // Keep hand raised state if either has it raised
          handRaised: current.handRaised || existing.handRaised,
          // Keep the most recent raisedAt timestamp
          raisedAt: current.raisedAt && existing.raisedAt 
            ? (current.raisedAt > existing.raisedAt ? current.raisedAt : existing.raisedAt)
            : (current.raisedAt || existing.raisedAt),
        };
      }
      return acc;
    }, [] as Participant[]);
    
    return unique;
  };

  // Clean up participants on component mount
  useEffect(() => {
    setParticipants(prev => {
      const cleaned = cleanParticipants(prev);
      if (cleaned.length !== prev.length) {
        console.log("Cleaned up participants on mount. Before:", prev.length, "After:", cleaned.length);
      }
      return cleaned;
    });
  }, []);

  // Periodic cleanup to ensure participants list stays clean
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setParticipants(prev => {
        const cleaned = cleanParticipants(prev);
        if (cleaned.length !== prev.length) {
          console.log("Periodic cleanup: Before:", prev.length, "After:", cleaned.length);
        }
        return cleaned;
      });
    }, 30000); // Clean every 30 seconds

    return () => clearInterval(cleanupInterval);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]); // Scroll when messages update

  return (
    <div className="h-screen bg-gray-100">
      {/* Hand Raise Notification */}
      {handRaiseNotification && (
        <div className="fixed top-4 right-4 z-50 bg-blue-500 text-white p-4 rounded-lg shadow-lg animate-pulse">
          <div className="flex items-center space-x-3">
            <i className="fas fa-hand-paper text-2xl"></i>
            <div>
              <p className="font-semibold">Hand Raised!</p>
              <p className="text-sm opacity-90">
                {handRaiseNotification.studentName} raised their hand
              </p>
            </div>
            <button
              onClick={() => setHandRaiseNotification(null)}
              className="ml-2 text-white hover:text-gray-200"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Top Navigation */}
      <div className="bg-white shadow-sm px-6 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-semibold">{sessionData?.title}</h1>
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
            Live
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">
            {participants.length} Participants
          </span>
          <span className="text-xs text-gray-500">
            ({remoteUsers.length} Video Streams)
          </span>
          
          {/* Recording Controls */}
          <div className="flex items-center space-x-2">
            {!isRecording ? (
              <button
                onClick={startRecording}
                className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600 transition-colors flex items-center space-x-1"
              >
                <i className="fas fa-record-vinyl"></i>
                <span>Start Recording</span>
              </button>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={stopRecording}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors flex items-center space-x-1"
                >
                  <i className="fas fa-stop"></i>
                  <span>Stop Recording</span>
                </button>
                <div className="flex items-center space-x-1 text-red-600">
                  <i className="fas fa-circle animate-pulse"></i>
                  <span className="text-sm font-medium">{formatRecordingTime(recordingTime)}</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Whiteboard Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleWhiteboardToggle}
              className={`px-3 py-1 text-white text-sm rounded transition-colors flex items-center space-x-1 ${
                isWhiteboardActive 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <i className="fas fa-chalkboard"></i>
              <span>{isWhiteboardActive ? 'Hide Whiteboard' : 'Show Whiteboard'}</span>
            </button>
          </div>
          
          <button
            onClick={() => setShowEndConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            End Session
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Panel - Video/Screen Share */}
        <div className="flex-1 p-4">
          <div className="bg-white rounded-lg shadow-sm h-full flex flex-col">
            {/* Video Grid Container */}
            <div className="video-grid-container flex-1 bg-gray-900 rounded-t-lg relative h-[calc(100vh-240px)] p-4">
              {/* Status Indicators */}
              <div className="absolute top-20 left-4 flex flex-col gap-2 z-30">
                {!isSocketConnected ? (
                  <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                    Disconnected
                  </div>
                ) : (
                  <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm flex items-center">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse mr-2"></div>
                    LIVE
                  </div>
                )}

                {/* Mute Indicator */}
                {!isMicOn && (
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm flex items-center">
                    <i className="fas fa-microphone-slash mr-2"></i>
                    Muted
                  </div>
                )}

              </div>

              {/* Video Grid */}
              <div className="h-full w-full grid gap-4" style={{
                // Calculate total grid items: 1 instructor + filtered students
                gridTemplateColumns: (() => {
                  const studentCount = participants.filter(p => {
                    const instructorId = sessionData?.instructorId?._id || sessionData?.instId || 'instructor';
                    const isInstructor = p.id === instructorId || 
                                       p.id === 'instructor' || 
                                       p.id === 'admin' ||
                                       p.name === 'admin' ||
                                       p.name === 'instructor' ||
                                       p.role === 'admin' ||
                                       p.role === 'instructor';
                    return !isInstructor;
                  }).length;
                  const totalItems = 1 + studentCount; // 1 instructor + students
                  
                  console.log(`=== GRID LAYOUT DEBUG ===`);
                  console.log(`Total participants: ${participants.length}`);
                  console.log(`Student count: ${studentCount}`);
                  console.log(`Total grid items: ${totalItems}`);
                  console.log(`=========================`);
                  
                  if (totalItems <= 1) return '1fr';
                  if (totalItems <= 2) return 'repeat(2, 1fr)';
                  if (totalItems <= 4) return 'repeat(2, 1fr)';
                  if (totalItems <= 9) return 'repeat(3, 1fr)';
                  return 'repeat(4, 1fr)';
                })(),
                gridTemplateRows: (() => {
                  const studentCount = participants.filter(p => {
                    const instructorId = sessionData?.instructorId?._id || sessionData?.instId || 'instructor';
                    const isInstructor = p.id === instructorId || 
                                       p.id === 'instructor' || 
                                       p.id === 'admin' ||
                                       p.name === 'admin' ||
                                       p.name === 'instructor' ||
                                       p.role === 'admin' ||
                                       p.role === 'instructor';
                    return !isInstructor;
                  }).length;
                  const totalItems = 1 + studentCount; // 1 instructor + students
                  
                  if (totalItems <= 1) return '1fr';
                  if (totalItems <= 2) return '1fr';
                  if (totalItems <= 4) return 'repeat(2, 1fr)';
                  if (totalItems <= 9) return 'repeat(3, 1fr)';
                  return 'repeat(4, 1fr)';
                })()
              }}>
                {/* Instructor Video (Always first) */}
                <div className="relative bg-black rounded-lg overflow-hidden border-2 border-blue-500">
                  <div
                    id="camera-video"
                    className="w-full h-full flex items-center justify-center"
                  />
                  
                  {/* Camera Off Indicator */}
                  {!isCameraOn && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                      <div className="text-center">
                        <i className="fas fa-video-slash text-4xl text-gray-400 mb-2"></i>
                        <p className="text-white text-sm">You (Camera Off)</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Network Quality Indicators */}
                  {networkQuality && (
                    <div className="absolute top-2 right-2 flex flex-col space-y-1">
                      <NetworkQualityIndicator 
                        quality={networkQuality.uplink} 
                        label="Upload" 
                      />
                      <NetworkQualityIndicator 
                        quality={networkQuality.downlink} 
                        label="Download" 
                      />
                    </div>
                  )}
                  
                  {/* Instructor Label */}
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                    You (Instructor)
                  </div>
                </div>

                {/* Student Videos - Show all participants, not just those with video */}
                {participants.filter(p => {
                  // Filter out the instructor/admin - they should not appear in student videos
                  const instructorId = sessionData?.instructorId?._id || sessionData?.instId || 'instructor';
                  const isInstructor = p.id === instructorId || 
                                     p.id === 'instructor' || 
                                     p.id === 'admin' ||
                                     p.name === 'admin' ||
                                     p.name === 'instructor' ||
                                     p.role === 'admin' ||
                                     p.role === 'instructor';
                  
                  console.log(`=== PARTICIPANT FILTER DEBUG ===`);
                  console.log(`Participant: ${p.name} (ID: ${p.id}, Role: ${p.role})`);
                  console.log(`Instructor ID: ${instructorId}`);
                  console.log(`Is Instructor: ${isInstructor}`);
                  console.log(`Should show: ${!isInstructor}`);
                  console.log(`===============================`);
                  
                  return !isInstructor;
                }).map((participant, index) => {
                  // Convert participant ID to numeric UID for Agora matching
                  // Extract numeric part from participant ID (same logic as mobile student)
                  const numbersOnly = participant.id.replace(/[^0-9]/g, '');
                  const baseUid = numbersOnly ? parseInt(numbersOnly.substring(0, numbersOnly.length > 8 ? 8 : numbersOnly.length), 10) : 12345678;
                  
                  // Check if this participant matches any remote user by trying different UID formats
                  const hasVideo = remoteUsers.some(user => {
                    const userUid = parseInt(user.uid);
                    const baseUidMatch = userUid === baseUid;
                    // Also check if the user UID is close to our base UID (within timestamp range)
                    const timestampMatch = Math.abs(userUid - baseUid) < 10000;
                    
                    console.log(`=== UID MATCHING DEBUG ===`);
                    console.log(`Participant: ${participant.name}`);
                    console.log(`Participant ID: ${participant.id}`);
                    console.log(`Base UID: ${baseUid}`);
                    console.log(`Remote User UID: ${user.uid} (${userUid})`);
                    console.log(`Base UID Match: ${baseUidMatch}`);
                    console.log(`Timestamp Match: ${timestampMatch}`);
                    console.log(`Final Match: ${baseUidMatch || timestampMatch}`);
                    console.log(`=========================`);
                    
                    return baseUidMatch || timestampMatch;
                  });
                  
                  // Debug logging
                  console.log(`=== PARTICIPANT DEBUG: ${participant.name} ===`);
                  console.log('Participant ID:', participant.id);
                  console.log('Numbers only:', numbersOnly);
                  console.log('Base UID:', baseUid);
                  console.log('Remote users:', remoteUsers.map(u => u.uid));
                  console.log('Has video:', hasVideo);
                  console.log('Video element ID will be:', `remote-video-${baseUid}`);
                  console.log('=====================================');
                  
                  return (
                    <div
                      key={participant.id}
                      className="relative bg-black rounded-lg overflow-hidden border border-white/20"
                    >
                      {/* Always create video element, show camera off indicator when no video */}
                      <div
                        id={`remote-video-${baseUid}`}
                        className="w-full h-full flex items-center justify-center"
                      />
                      
                      {/* Camera Off Overlay - only show when no video stream */}
                      {!hasVideo && (
                        <div className="camera-off-overlay absolute inset-0 flex items-center justify-center bg-gray-800">
                          <div className="text-center">
                            <i className="fas fa-user text-4xl text-gray-400 mb-2"></i>
                            <p className="text-white text-sm">{participant.name}</p>
                            <p className="text-gray-400 text-xs">Camera Off</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Student Label */}
                      <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                        {participant.name}
                      </div>
                    
                    {/* Network Quality Indicators for Student */}
                    {networkQuality && (
                      <div className="absolute top-2 right-2 flex flex-col space-y-1">
                        <div className="bg-black/70 text-white px-2 py-1 rounded text-xs">
                          <div className="flex items-center space-x-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              networkQuality.uplink >= 4 ? 'bg-green-400' :
                              networkQuality.uplink >= 3 ? 'bg-blue-400' :
                              networkQuality.uplink >= 2 ? 'bg-yellow-400' :
                              networkQuality.uplink >= 1 ? 'bg-orange-400' : 'bg-red-400'
                            }`}></div>
                            <span>Up: {getNetworkQualityStatus(networkQuality.uplink).status}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              networkQuality.downlink >= 4 ? 'bg-green-400' :
                              networkQuality.downlink >= 3 ? 'bg-blue-400' :
                              networkQuality.downlink >= 2 ? 'bg-yellow-400' :
                              networkQuality.downlink >= 1 ? 'bg-orange-400' : 'bg-red-400'
                            }`}></div>
                            <span>Down: {getNetworkQualityStatus(networkQuality.downlink).status}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                      {/* Hand Raised Indicator */}
                      {participant.handRaised && (
                        <div className="absolute top-2 left-2 bg-blue-500 text-white p-1 rounded-full">
                          <i className="fas fa-hand-paper text-sm"></i>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Video Controls - now part of the container div */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 flex justify-center space-x-4 rounded-b-lg">
                <button
                  onClick={toggleMic}
                  className={`p-3 rounded-full relative group transition-all duration-200 ${
                    isMicOn
                      ? "bg-white/10 hover:bg-white/20"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  <i
                    className={`fas fa-microphone${
                      isMicOn ? "" : "-slash"
                    } text-white`}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                    {isMicOn ? "Turn Off Microphone" : "Turn On Microphone"}
                  </span>
                </button>

                <button
                  onClick={toggleCamera}
                  className={`p-3 rounded-full relative group transition-all duration-200 ${
                    isCameraOn
                      ? "bg-white/10 hover:bg-white/20"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  <i
                    className={`fas fa-video${
                      isCameraOn ? "" : "-slash"
                    } text-white`}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                    {isCameraOn ? "Turn Off Camera" : "Turn On Camera"}
                  </span>
                </button>

                <button
                  onClick={
                    isScreenSharing ? stopScreenSharing : startScreenSharing
                  }
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full relative group"
                >
                  <i
                    className={`fas fa-desktop text-${
                      isScreenSharing ? "green-400" : "white"
                    }`}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                    {isScreenSharing ? "Stop Sharing" : "Share Screen"}
                  </span>
                </button>

                <button
                  onClick={handleWhiteboardToggle}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full relative group"
                >
                  <i
                    className={`fas fa-chalkboard text-${
                      isWhiteboardActive ? "green-400" : "white"
                    }`}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                    {isWhiteboardActive ? "Hide Whiteboard" : "Show Whiteboard"}
                  </span>
                </button>
              </div>

              <div 
                className="whiteboard-container" 
                style={{ 
                  display: isWhiteboardActive ? 'block' : 'none',
                  width: '100%',
                  height: '400px',
                  backgroundColor: 'white',
                  border: '1px solid #ccc'
                }}
              >
                <Whiteboard
                  isActive={isWhiteboardActive}
                  onDraw={handleWhiteboardDraw}
                  onClose={handleWhiteboardToggle}
                  onClear={handleWhiteboardClear}
                />
              </div>
            </div>

            {/* Presentation Tools */}
            <div className="p-4 border-t flex justify-between items-center">
              <div className="flex space-x-3">
                <button
                  onClick={handleWhiteboardToggle}
                  className="p-2 hover:bg-gray-100 rounded relative group"
                >
                  <i
                    className={`fas fa-chalkboard text-${
                      isWhiteboardActive ? "blue-600" : "gray-600"
                    }`}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                    {isWhiteboardActive ? "Hide Whiteboard" : "Show Whiteboard"}
                  </span>
                </button>

                {/* <div className="relative group">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <i className="fas fa-file-pdf text-gray-600" />
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                      Share PDF
                    </span>
                  </button>
                </div> */}

                {/* <button
                  onClick={() => setShowPollCreator(true)}
                  className="p-2 hover:bg-gray-100 rounded relative group"
                >
                  <i className="fas fa-poll text-gray-600" />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                    Create Poll
                  </span>
                </button> */}
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowRaisedHands(!showRaisedHands)}
                  className={`p-2 hover:bg-gray-100 rounded flex items-center space-x-2 relative ${
                    raisedHands.length > 0 ? 'bg-blue-50 border-2 border-blue-300' : ''
                  }`}
                >
                  <i className={`fas fa-hand-paper ${raisedHands.length > 0 ? 'text-blue-600' : 'text-gray-600'}`} />
                  <span className={`text-sm ${raisedHands.length > 0 ? 'text-blue-700 font-semibold' : ''}`}>
                    {raisedHands.length} Raised Hands
                  </span>
                  {raisedHands.length > 0 && (
                    <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                      {raisedHands.length}
                    </div>
                  )}
                </button>

                {showRaisedHands && raisedHands.length > 0 && (
                  <div className="absolute bottom-full right-0 mb-2 w-64 bg-white rounded-lg shadow-lg border z-50">
                    <div className="p-3 border-b">
                      <h3 className="font-medium">Raised Hands</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {raisedHands
                        .sort(
                          (a, b) =>
                            (a.raisedAt?.getTime() || 0) -
                            (b.raisedAt?.getTime() || 0)
                        )
                        .map((participant) => (
                          <div
                            key={participant.id}
                            className="p-3 hover:bg-gray-50 flex items-center justify-between border-b last:border-b-0"
                          >
                            <div>
                              <p className="font-medium">{participant.name || "Unknown User"}</p>
                              <p className="text-xs text-gray-500">
                                {participant.raisedAt &&
                                  `Raised ${formatTimeAgo(
                                    participant.raisedAt
                                  )}`}
                              </p>
                            </div>
                            <button
                              onClick={() => handleLowerHand(participant.id)}
                              className="text-xs text-blue-600 hover:text-blue-700"
                            >
                              Lower Hand
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Chat & Participants */}
        <div className="w-80 bg-white border-l">
          <div className="h-full flex flex-col">
            {/* Tabs */}
            <div className="flex border-b">
              <button
                onClick={() => setShowParticipants(false)}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  !showParticipants
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Chat
              </button>
              <button
                onClick={() => setShowParticipants(true)}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  showParticipants
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Participants
              </button>
            </div>

            {/* Content */}

            {/* Chat Input - Only show when chat tab is active */}
            {!showParticipants ? (
              <>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-4">
                    {messages.map((msg, index) => (
                      <div key={`${msg.sender.userId}-${typeof msg.timestamp === 'string' ? msg.timestamp : (msg.timestamp instanceof Date ? msg.timestamp.getTime() : msg.timestamp)}-${index}`} className="flex items-start space-x-3">
                        <img
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                            msg.sender.name
                          )}`}
                          alt={msg.sender.name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <p className="text-sm font-medium">
                            {msg.sender.name}
                          </p>
                          <p className="text-sm text-gray-600">{msg.message}</p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                <div className="p-4 border-t">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!chatMessage.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-3 p-4">
      {/* Instructor Controls */}
      <div className="border-b pb-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Instructor Controls</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleMuteAllStudents}
            className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
          >
            Mute All
          </button>
          <button
            onClick={handleUnmuteAllStudents}
            className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
          >
            Unmute All
          </button>
          <button
            onClick={handleLowerAllHands}
            className="px-3 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600 transition-colors"
            disabled={raisedHands.length === 0}
          >
            Lower All Hands
          </button>
          
          {/* Recording Controls */}
          <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-300">
            {!isRecording ? (
              <button
                onClick={startRecording}
                className="px-3 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-colors flex items-center space-x-1"
              >
                <i className="fas fa-record-vinyl"></i>
                <span>Start Recording</span>
              </button>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={stopRecording}
                  className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors flex items-center space-x-1"
                >
                  <i className="fas fa-stop"></i>
                  <span>Stop Recording</span>
                </button>
                <div className="flex items-center space-x-1 text-red-600">
                  <i className="fas fa-circle animate-pulse"></i>
                  <span className="text-xs font-medium">{formatRecordingTime(recordingTime)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

                {/* Debug Panel */}
                <div className="mb-4 p-2 bg-gray-100 rounded text-xs">
                  <div><strong>Debug Info:</strong></div>
                  <div>Participants: {participants.length}</div>
                  <div>Remote Users: {remoteUsers.length}</div>
                  <div>Socket Connected: {isSocketConnected ? 'Yes' : 'No'}</div>
                  <div>Participant IDs: {participants.map(p => p.id).join(', ')}</div>
                  <div>Remote UIDs: {remoteUsers.map(u => u.uid).join(', ')}</div>
                  
                  {/* Network Quality Summary */}
                  {networkQuality && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div><strong>Network Quality:</strong></div>
                      <div>Upload: {getNetworkQualityStatus(networkQuality.uplink).status} ({networkQuality.uplink}/5)</div>
                      <div>Download: {getNetworkQualityStatus(networkQuality.downlink).status} ({networkQuality.downlink}/5)</div>
                      <div>Last Update: {networkQuality.timestamp.toLocaleTimeString()}</div>
                    </div>
                  )}
                </div>

                {/* Participants List */}
                <div className="space-y-2">
                {participants.map((participant) => (
                  <div
                    key={participant.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                            participant.name || "User"
                        )}`}
                          alt={participant.name || "User"}
                        className="w-8 h-8 rounded-full"
                      />
                        <span className="font-medium">{participant.name || "Unknown User"}</span>
                    {participant.handRaised && (
                          <i className="fas fa-hand-paper text-blue-500" title="Hand raised" />
                        )}
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleMuteStudent(participant.id)}
                          className="px-2 py-1 bg-red-400 text-white text-xs rounded hover:bg-red-500 transition-colors"
                          title="Mute student"
                        >
                          <i className="fas fa-microphone-slash"></i>
                        </button>
                        <button
                          onClick={() => handleUnmuteStudent(participant.id)}
                          className="px-2 py-1 bg-green-400 text-white text-xs rounded hover:bg-green-500 transition-colors"
                          title="Unmute student"
                        >
                          <i className="fas fa-microphone"></i>
                        </button>
                      </div>
                  </div>
                ))}
                </div>
                {joinedParticipants.map((participant) => (
                  <div key={participant.id} className="text-sm text-green-600">
                    {participant.name || "Unknown User"} joined
                  </div>
                ))}
                {leftParticipants.map((participant) => (
                  <div key={participant.id} className="text-sm text-red-600">
                    {participant.name || "Unknown User"} left
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showEndConfirm}
        onClose={() => setShowEndConfirm(false)}
        onConfirm={handleEndSessionWithRecording}
        title="End Session"
        message="Are you sure you want to end this session? All participants will be disconnected."
      />

      <PollCreator
        isOpen={showPollCreator}
        onClose={() => setShowPollCreator(false)}
        onCreatePoll={handleCreatePoll}
      />

      {showPdfViewer && currentPdf && (
        <PdfViewer
          file={currentPdf}
          onClose={() => {
            setShowPdfViewer(false);
            setCurrentPdf(null);
          }}
        />
      )}

      {!isSocketConnected && (
        <div className="bg-red-100 text-red-700 px-4 py-2 rounded-md mb-4">
          Disconnected from server. Trying to reconnect...
        </div>
      )}

      {/* Upload Recording Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Upload Recording as Course Lesson</h3>
            <p className="text-gray-600 mb-4">
              You have a recorded classroom session. Would you like to upload it as a lesson for students to review later?
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center">
                <i className="fas fa-info-circle text-blue-500 mr-2"></i>
                <span className="text-sm text-blue-700">
                  This will create a new lesson in your course that students can access anytime.
                </span>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lesson Title
                </label>
                <input
                  type="text"
                  id="lessonTitle"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter lesson title..."
                  defaultValue={`Recording from ${new Date().toLocaleDateString()}`}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  id="lessonDescription"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Enter lesson description..."
                />
              </div>

              {/* Section Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add to Section
                </label>
                
                {/* Toggle between existing sections and new section */}
                <div className="flex space-x-4 mb-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="sectionOption"
                      checked={!isCreatingNewSection}
                      onChange={() => setIsCreatingNewSection(false)}
                      className="mr-2"
                    />
                    <span className="text-sm">Existing Section</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="sectionOption"
                      checked={isCreatingNewSection}
                      onChange={() => setIsCreatingNewSection(true)}
                      className="mr-2"
                    />
                    <span className="text-sm">Create New Section</span>
                  </label>
                </div>

                {!isCreatingNewSection ? (
                  // Existing sections dropdown
                  <select
                    value={selectedSectionId}
                    onChange={(e) => setSelectedSectionId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a section...</option>
                    {courseSections.map((section, index) => (
                      <option key={section._id || index} value={section._id || index}>
                        {section.title} ({section.lessons?.length || 0} lessons)
                      </option>
                    ))}
                  </select>
                ) : (
                  // New section form
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Section Title
                      </label>
                      <input
                        type="text"
                        value={newSectionTitle}
                        onChange={(e) => setNewSectionTitle(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter section title..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Section Description (Optional)
                      </label>
                      <textarea
                        value={newSectionDescription}
                        onChange={(e) => setNewSectionDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={2}
                        placeholder="Enter section description..."
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setRecordedBlob(null); // Clear the recorded blob
                  // Reset form state
                  setSelectedSectionId('');
                  setNewSectionTitle('');
                  setNewSectionDescription('');
                  setIsCreatingNewSection(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Don't Upload
              </button>
              <button
                onClick={() => {
                  const title = (document.getElementById('lessonTitle') as HTMLInputElement)?.value || 'Untitled Lesson';
                  const description = (document.getElementById('lessonDescription') as HTMLTextAreaElement)?.value || '';
                  uploadRecording(title, description);
                }}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Creating Lesson...' : 'Create Lesson'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to format time
const formatTimeAgo = (date: Date) => {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
};

export default InstructorClassroom;
